/**
 * The scene's revision, sent with every tool answer (`SceneState.revision`): equal revisions, the same authored data and
 * world transforms. The gateway's `review_scene` compares a screenshot's revision with the current one, so a review
 * never compares a scene that has changed since (ml-services src/agent/review.ts). The editor camera is not part of it.
 *
 * It costs one export build and one walk of the transforms per tool answer: about 17 ms on a 451-object, 25-page LEGO
 * deck (255 KB of export data; measured 2026-10-30 in the app). Accepted, as a cheaper id would need a change counter
 * that every edit path (undo, the inspector, scripts) keeps, which none does yet.
 */

import type { TraceScene } from 'trace-viewer/scene';

/** Two 32-bit FNV-1a hashes with different seeds, as hex: a short id for a long state, not a security boundary. */
function fnv1a(text: string): string {
    let a = 0x811c9dc5, b = 0x050c5d1f;
    for (let i = 0; i < text.length; i++) {
        const c = text.charCodeAt(i);
        a = Math.imul(a ^ c, 0x01000193);
        b = Math.imul(b ^ c, 0x01000193);
    }
    return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

/** The revision, or none when the scene cannot be read now: an answer still goes, and the gateway treats the revision as unknown. */
export function sceneRevision(scene: TraceScene): string | undefined {
    try {
        return fnv1a(JSON.stringify({
            authored: scene._buildExportData().data,
            transforms: scene.getSceneObjects().map(o => {
                o.transform.computeWorldMatrix(true);
                return [o.getPath(), o.transform.getWorldMatrix().asArray()];
            }),
        }));
    } catch {
        return undefined;
    }
}
